import { BANNER_SIZES, SDK_CAPABILITIES, SDK_LIMITS, SDK_ERRORS } from '@riz/contracts/sdk';
import { rizError } from './errors.ts';
import type { HandshakeRequest, HandshakeResponse, Operation } from './protocol.ts';
import type { Transport } from './transport.ts';

export interface DemoScript {
  /** Force this error code on the next matching call, then clear it. */
  nextError?: { code: string; match?: string } | null;
  ads?: {
    outcome?: 'completed' | 'skipped' | 'no_fill' | 'error' | 'cooldown';
    /** Simulated watch time before the outcome resolves. */
    durationMs?: number;
    hasBlocker?: boolean;
  };
  banner?: { outcome?: 'filled' | 'no_fill' | 'cooldown' | 'not_visible' };
  purchases?: { outcome?: 'completed' | 'cancelled' | 'failed' };
  social?: { roomJoinable?: boolean; outcome?: 'ok' | 'unavailable' };
  player?: { isGuest?: boolean; displayName?: string; rpBalance?: number };
  latencyMs?: number;
}

export interface DemoLogEntry {
  at: string;
  kind: Operation['kind'];
  name: string;
  payload: Record<string, unknown>;
  result: 'resolved' | 'rejected';
  detail: unknown;
}

const DEMO_CATALOGUE = [
  { sku: 'demo.coins.100', title: '100 Coins', price: '0.99', currency: 'USD' },
  { sku: 'demo.remove_ads', title: 'Remove Ads', price: '2.99', currency: 'USD' },
];

/**
 * Local mode. Every branch a live session can produce is reachable here,
 * because a creator cannot otherwise build the failure paths: an ad that never
 * fills, a quota that fills up, a purchase the player cancels.
 *
 * Nothing leaves the page and no timing is faked away — a demo rewarded ad
 * really does take its configured duration, so pause and mute handling is
 * genuinely exercised.
 */
export class DemoTransport implements Transport {
  readonly kind = 'demo' as const;

  private readonly log: DemoLogEntry[] = [];
  private readonly store = new Map<string, string>();
  private script: DemoScript = {};
  private bestScore = 0;
  private bannerRefreshes = 0;
  private signalHandler: ((signal: string, detail: Record<string, unknown>) => void) | null = null;

  constructor(script: DemoScript = {}) {
    this.script = script;
  }

  configure(script: DemoScript): void {
    this.script = { ...this.script, ...script };
  }

  entries(): DemoLogEntry[] {
    return [...this.log];
  }

  async connect(request: HandshakeRequest): Promise<HandshakeResponse> {
    await this.latency();
    return {
      protocol: 2,
      environment: 'local',
      runId: `demo-run-${Date.now().toString(36)}`,
      nonce: 'demo-nonce',
      player: {
        id: 'demo-player',
        displayName: this.script.player?.displayName ?? 'Demo Player',
        avatarUrl: null,
        rpBalance: this.script.player?.rpBalance ?? 0,
        gamesPlayed: 0,
        isGuest: this.script.player?.isGuest ?? true,
        personalBest: 0,
      },
      game: {
        id: 'demo-game',
        slug: 'demo',
        title: request.gameKey,
        verified: false,
        rewardsEnabled: false,
        sitelock: false,
      },
      limits: { ...SDK_LIMITS, ...SDK_LIMITS },
      capabilities: [...SDK_CAPABILITIES],
      deprecation: null,
    };
  }

  async send(operation: Operation): Promise<Record<string, unknown>> {
    await this.latency();
    try {
      const result = await this.route(operation);
      this.record(operation, 'resolved', result);
      return result;
    } catch (error) {
      this.record(operation, 'rejected', error);
      throw error;
    }
  }

  beacon(operations: Operation[]): void {
    for (const operation of operations) this.record(operation, 'resolved', { beacon: true });
  }

  notify(): void {}

  onSignal(handler: (signal: string, detail: Record<string, unknown>) => void): void {
    this.signalHandler = handler;
  }

  dispose(): void {
    this.signalHandler = null;
  }

  private async route(operation: Operation): Promise<Record<string, unknown>> {
    const forced = this.takeForcedError(operation.name);
    if (forced) throw forced;

    if (operation.kind === 'event') return this.handleEvent(operation);
    if (operation.kind === 'query') return this.handleQuery(operation);
    return this.handleCommand(operation);
  }

  private handleEvent(operation: Operation): Record<string, unknown> {
    const submitted = Number(operation.payload.score);
    if (Number.isFinite(submitted)) this.bestScore = Math.max(this.bestScore, Math.trunc(submitted));
    if (operation.name === 'game_ended') {
      return {
        accepted: true,
        completed: true,
        demo: true,
        score: this.bestScore,
        rpEarned: Math.min(SDK_LIMITS.maxRpPerRun, Math.floor(this.bestScore / SDK_LIMITS.scorePerRp)),
        rpBalance: 0,
        rewardsPaused: true,
      };
    }
    return { accepted: true, demo: true, score: this.bestScore };
  }

  private handleQuery(operation: Operation): Record<string, unknown> {
    if (operation.name === 'player') {
      return { id: 'demo-player', displayName: 'Demo Player', isGuest: true, personalBest: this.bestScore, demo: true };
    }
    return { game: 'demo', entries: [], you: null, demo: true };
  }

  private async handleCommand(operation: Operation): Promise<Record<string, unknown>> {
    switch (operation.name) {
      case 'ads/request':
        return this.handleAdRequest(operation);
      case 'ads/available':
        return { available: (this.script.ads?.outcome ?? 'completed') !== 'no_fill', format: operation.payload.format };
      case 'ads/blocker':
        return { hasBlocker: this.script.ads?.hasBlocker ?? false };
      case 'banner/request':
        return this.handleBannerRequest(operation);
      case 'banner/clear':
        return { cleared: true };
      case 'data/get':
        return { value: this.store.get(String(operation.payload.key)) ?? null };
      case 'data/set':
        return this.handleDataSet(operation);
      case 'data/remove':
        this.store.delete(String(operation.payload.key));
        return { removed: true };
      case 'data/clear':
        this.store.clear();
        return { cleared: true };
      case 'purchases/catalogue':
        return { items: DEMO_CATALOGUE };
      case 'purchases/buy':
        return this.handlePurchase(operation);
      case 'purchases/entitlements':
        return { entitlements: [] };
      case 'purchases/consume':
        return { consumed: true };
      case 'social/room':
        if (this.script.social?.outcome === 'unavailable') {
          throw rizError(SDK_ERRORS.roomUnavailable, 'Demo room registry is unavailable.');
        }
        return { roomId: operation.payload.roomId, joinable: this.script.social?.roomJoinable ?? true };
      case 'social/invite':
        return { url: `https://demo.local/play?rizInvite=${encodeURIComponent(JSON.stringify(operation.payload))}` };
      case 'social/share':
        return { shared: true };
      case 'user/auth-prompt':
        return { id: 'demo-player', displayName: 'Demo Player', isGuest: false };
      default:
        throw rizError(SDK_ERRORS.invalidEvent, `Demo mode has no handler for "${operation.name}".`);
    }
  }

  private async handleAdRequest(operation: Operation): Promise<Record<string, unknown>> {
    const outcome = this.script.ads?.outcome ?? 'completed';
    if (outcome === 'no_fill') throw rizError(SDK_ERRORS.adNoFill, 'No demo ad available.');
    if (outcome === 'cooldown') throw rizError(SDK_ERRORS.adCooldown, 'Demo ad is on cooldown.');
    if (outcome === 'error') throw rizError(SDK_ERRORS.serverError, 'Demo ad failed to render.');

    const durationMs = this.script.ads?.durationMs ?? 5_000;
    this.signalHandler?.('ad-started', { placement: operation.payload.placement });
    await wait(durationMs);
    this.signalHandler?.('ad-finished', { placement: operation.payload.placement, outcome });

    if (outcome === 'skipped') {
      return { completed: false, rewarded: false, skipped: true, watchedMs: Math.floor(durationMs / 2), demo: true };
    }
    return { completed: true, rewarded: true, rpEarned: 0, watchedMs: durationMs, demo: true };
  }

  private handleBannerRequest(operation: Operation): Record<string, unknown> {
    const outcome = this.script.banner?.outcome ?? 'filled';
    if (outcome === 'no_fill') throw rizError(SDK_ERRORS.unfilled, 'No demo banner available.');
    if (outcome === 'cooldown') throw rizError(SDK_ERRORS.bannerCooldown, 'Demo banner is on cooldown.');
    if (outcome === 'not_visible') throw rizError(SDK_ERRORS.bannerNotVisible, 'Demo banner container is not visible.');
    if (this.bannerRefreshes >= SDK_LIMITS.maxBannerRefreshPerSession) {
      throw rizError(SDK_ERRORS.bannerMaxRefresh, 'Demo banner refresh ceiling reached.');
    }
    this.bannerRefreshes += 1;
    const size = String(operation.payload.size ?? 'medium') as keyof typeof BANNER_SIZES;
    return { filled: true, size: BANNER_SIZES[size] ?? BANNER_SIZES.medium, demo: true };
  }

  private handleDataSet(operation: Operation): Record<string, unknown> {
    const key = String(operation.payload.key);
    const value = String(operation.payload.value ?? '');
    const projected = [...this.store.entries()]
      .filter(([existing]) => existing !== key)
      .reduce((total, [k, v]) => total + k.length + v.length, 0) + key.length + value.length;
    if (projected > SDK_LIMITS.maxDataBytes) {
      throw rizError(
        SDK_ERRORS.dataLimitExceeded,
        `Saved data cannot exceed ${SDK_LIMITS.maxDataBytes} bytes.`,
        { limit: SDK_LIMITS.maxDataBytes, projected },
      );
    }
    this.store.set(key, value);
    return { saved: true, bytes: projected };
  }

  private handlePurchase(operation: Operation): Record<string, unknown> {
    const outcome = this.script.purchases?.outcome ?? 'completed';
    if (outcome === 'cancelled') throw rizError(SDK_ERRORS.purchaseCancelled, 'The demo player cancelled.');
    if (outcome === 'failed') throw rizError(SDK_ERRORS.purchaseFailed, 'The demo payment failed.');
    return { status: 'completed', sku: operation.payload.sku, entitlementId: `demo-ent-${Date.now().toString(36)}` };
  }

  private takeForcedError(name: string) {
    const forced = this.script.nextError;
    if (!forced) return null;
    if (forced.match && !name.includes(forced.match)) return null;
    this.script = { ...this.script, nextError: null };
    return rizError(forced.code, `Demo mode forced "${forced.code}".`);
  }

  private record(operation: Operation, result: 'resolved' | 'rejected', detail: unknown) {
    this.log.push({
      at: new Date().toISOString(),
      kind: operation.kind,
      name: operation.name,
      payload: operation.payload,
      result,
      detail,
    });
  }

  private latency() {
    return wait(this.script.latencyMs ?? 0);
  }
}

function wait(ms: number) {
  return ms > 0 ? new Promise((resolve) => setTimeout(resolve, ms)) : Promise.resolve();
}
